import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { userService } from '../services/userService';
import { authService } from '../services/authService';
import { venueService } from '../services/venueService';
import { UserListItem, UserFilters, CreateUserRequest } from '../types/user';
import { UserRole } from '../types/auth';
import { isProfileComplete } from '../utils/profileUtils';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { isForintInputAccepted, parseForintInput, forintFieldValue, isUnfinishedForintDraft, unfinishedForintMessage } from '../utils/money';
// A HIBA-SZÖVEGEK A KÖZÖS FORDÍTÓN [B-W6, 2026-09-16] — lásd `utils/apiErrors.ts`. A korábbi
// nyers `err.response?.data?.message || '…'` minta HÁROM esetet mosott össze: (1) a szerver
// megmondta magyarul, mi a baj, (2) a szerver válaszolt, de üzenet nélkül (pl. 403, vagy a
// nyolc kontroller BURKOLT `{success:false,error:{message}}` törzse, illetve a modell-validációs
// `{errors:{mező:[…]}}` alak), (3) a kérés EL SEM ÉRTE a szervert. A (2) esetben a MAGYAR indoklás
// sosem jutott ki, a (3) esetben pedig a felhasználó szerverhibát látott offline állapot helyett.
// [3. javító kör, F-1, 2026-10-03] Ma a lejárat-tudatos burkolókon át (`utils/saveErrors.ts`, belül
// `apiErrorMessage`): a mentés 401-e zárral a „megmaradtak" mondatot adja.
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
// A JELSZÓ-SZABÁLY KLIENS-OLDALI TÜKRE [B-W7, 2026-09-16] — ugyanaz a modul, mint a
// regisztráción és a jelszó-cserénél; az pedig a SZERVER `DTOs/PasswordPolicy.cs`-éből van
// tükrözve. Így az admin ELŐRE látja, mit kell beírnia, nem a szerver 400-ából, próbálgatással.
import { PASSWORD_RULE_MESSAGE, passwordPolicyError } from '../utils/passwordPolicy';

/**
 * A szöveges keresés késleltetése [Web UX 3. csomag (C), 2026-10-04]: ennyi csend után megy ki a
 * kérés. Exportált, hogy a teszt ne tippeljen (`UserManagement.test.tsx`).
 */
export const SEARCH_DEBOUNCE_MS = 300;

interface UserManagementProps {
  onEditUser?: (userId: string) => void;
}

/**
 * Az ÜRES „Új felhasználó" űrlap EGY forrásból.
 *
 * MIÉRT FACTORY: a mentetlen-változás védelem ALAPVONALA és a dialógus nyitáskori NULLÁZÁSA
 * ugyanezt az objektumot használja. Ha a két helyen külön írnánk le a kezdőértékeket, egyetlen
 * eltérő kulcs (pl. az egyik oldalon `isActive: true`, a másikon hiányzik) HAMIS „mentetlen
 * adat" jelzést okozna — onnantól MINDEN érintetlen ablak bezárása rákérdezne, és a
 * felhasználó megszokná, hogy átkattint a kérdésen. A kész minták (adatlap, hír-szerkesztő)
 * tanulsága ugyanez.
 */
const createEmptyUserForm = () => ({
  name: '',
  email: '',
  phone: '',
  userRoleId: '',
  venueId: undefined as string | undefined,
  bankAccount: '',
  billingName: '',
  billingZipCode: '',
  billingCity: '',
  billingStreetAddress: '',
  hourlyRate: undefined as number | undefined,
  privateLessonFee: undefined as number | undefined,
  password: '',
});

/**
 * A felhasználó szerepkör-AZONOSÍTÓJA a betöltött szerepkör-listából.
 *
 * EGY FORRÁS: a nyitáskori betöltő (`userToForm`) ÉS a KÉSVE megjövő lista utólagos kitöltése is
 * ezt hívja — így a kettő nem csúszhat el egymástól (elcsúszva hamis „mentetlen" állapot lenne).
 * Üres lista esetén `''`: ilyenkor a választó még nem tud érvényes értéket felvenni.
 */
const resolveUserRoleId = (userRoles: UserRole[], user: UserListItem) =>
  userRoles.find(r => r.name === user.userRole)?.userRoleId || '';

/** A LÉTEZŐ felhasználó adatai űrlap-alakban — szintén egy forrásból (alapvonal + űrlap). */
const userToForm = (user: UserListItem, userRoles: UserRole[]) => ({
  name: user.name,
  email: user.email,
  phone: user.phone,
  userRoleId: resolveUserRoleId(userRoles, user),
  venueId: user.venueId || undefined,
  bankAccount: user.bankAccount || '',
  billingName: user.billingName || '',
  billingZipCode: user.billingZipCode || '',
  billingCity: user.billingCity || '',
  billingStreetAddress: user.billingStreetAddress || '',
  hourlyRate: user.hourlyRate,
  privateLessonFee: user.privateLessonFee,
  isActive: user.isActive,
  isApproved: user.isApproved,
});

const UserManagement: React.FC<UserManagementProps> = ({ onEditUser }) => {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [filters, setFilters] = useState<UserFilters>({});
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view'>('view');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  /**
   * KERESÉS: AZONNALI MEZŐ, KÉSLELTETETT LEKÉRDEZÉS, ELAVULT VÁLASZ ELDOBÁSA
   * [Web UX 3. csomag (C), 2026-10-04 — `Documents/WEB_UX_JAVASLATOK_2026-10-02.md` 1.5]
   *
   * MI VOLT A BAJ:
   *  1. A keresés NEM ugrott az első oldalra: a 3. oldalon gépelve a szerver a szűrt lista 3.
   *     oldalát adta — ami 1–2 találatnál ÜRES. Az admin azt hitte, nincs ilyen felhasználó.
   *  2. Minden leütés KÜLÖN kérés volt (`[filters, page, rowsPerPage]` effekt), és a válaszok
   *     tetszőleges sorrendben érkezhettek: egy lassú, RÉGEBBI („Ki") válasz felülírhatta a
   *     frissebbet („Kiss D"), tehát a lista nem a mezőben álló szövegre szólt.
   * A JAVÍTÁS:
   *  • a mező SAJÁT, azonnali állapotot kap (`searchInput`) — a gépelés nem akad;
   *  • a szöveg 300 ms csend után kerül a lekérdezésbe (`SEARCH_DEBOUNCE_MS`), a legördülő szűrők
   *    (szerepkör, helyszín) AZONNAL hatnak;
   *  • minden szűrő-váltás ÉS a „Törlés" UGYANABBAN a frissítésben állítja a lapot 0-ra (egy
   *    eseménykezelőn / időzítőn belül a React egy renderbe vonja össze) → EGY kérés megy ki, az
   *    1. oldalra — nem kettő (a régi lapra, majd az 1.-re);
   *  • az elavult válasz sorszámmal eldobva (minta: `EventAttendance.tsx` `fetchSeqRef`/`isStale`).
   */
  const [searchInput, setSearchInput] = useState('');
  const loadSeqRef = useRef(0);
  // A már ALKALMAZOTT szűrő a késleltető számára (az effekt csak a `searchInput`-ra figyel).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const next = searchInput || undefined;
    // Nincs változás (betöltéskor, vagy ha a „Törlés" már kiürítette) → nincs időzítő, nincs
    // lapozás-visszaállítás: különben a betöltés utáni első 300 ms-ban lapozó admin visszaesne.
    if (next === filtersRef.current.search) return;
    const timer = setTimeout(() => {
      // EGY időzítő-hívásban → EGY render → EGY kérés, már az 1. oldalra.
      setFilters(prev => ({ ...prev, search: next }));
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadUsers();
  }, [filters, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUserRoles();
    loadVenues();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    // Sorszám: csak a LEGUTÓBB indított kérés válasza írhat az állapotba [Web UX 3. csomag (C)].
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;
    try {
      setLoading(true);
      setError('');
      const result = await userService.getUsers(filters, {
        pageNumber: page + 1, // MUI Table uses 0-based index, API uses 1-based
        pageSize: rowsPerPage
      });
      if (isStale()) return;
      setUsers(result.data);
      setTotalCount(result.totalCount || result.data.length);
    } catch (err: any) {
      if (isStale()) return;
      setError(requestErrorMessage(err, 'Hiba történt a felhasználók betöltésekor'));
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const loadUserRoles = async () => {
    try {
      const roles = await authService.getUserRoles();
      setUserRoles(roles);
    } catch (err) {
      console.error('Error loading user roles:', err);
    }
  };

  const loadVenues = async () => {
    try {
      const venuesData = await venueService.getActiveVenues();
      setVenues(venuesData);
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  /** Legördülő szűrő (szerepkör, helyszín): AZONNAL hat, és UGYANABBAN a renderben az 1. oldalra ugrik. */
  const handleFilterChange = (field: keyof UserFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
    setPage(0);
  };

  /** „Törlés": a gépelt szöveg, az alkalmazott szűrők és a lap EGYÜTT állnak vissza (egy kérés). */
  const clearFilters = () => {
    setSearchInput('');
    setFilters({});
    setPage(0);
  };

  const openDialog = (type: typeof dialogType, user?: UserListItem) => {
    setDialogType(type);
    setSelectedUser(user || null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  /**
   * @returns [7. javító kör — validátor F3, 2026-10-04] a MENTÉS hibaszövege, ha a dialógus nyitva
   *   maradt (a `UserDialog` a saját hiba-sávjában mutatja), különben `null`. KORÁBBAN a hiba csak a
   *   lap tetejei sávba került, ami a modális dialógus MÖGÖTT volt — a dialógus `formError`-ja
   *   szerverhibánál üres maradt, a felhasználó semmit nem látott.
   */
  const handleUserAction = async (action: 'create' | 'edit', data?: any): Promise<string | null> => {
    let closed = false;
    try {
      setError('');

      switch (action) {
        case 'create':
          await userService.createUser(data);
          showSuccess('A felhasználó létrehozva.');
          break;
        case 'edit':
          if (selectedUser) {
            await userService.updateUser(selectedUser.userId, data);
            showSuccess('A felhasználó módosítva.');
          }
          break;
      }

      closeDialog();
      closed = true;
      await loadUsers();
      return null;
    } catch (err: any) {
      // [validátor M-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal egyező mondat
      const message = saveErrorMessage(err, 'Hiba történt a művelet során');
      setError(message);
      return closed ? null : message;
    }
  };

  const handleDeleteUser = async (user: UserListItem) => {
    const confirmed = await confirm({
      title: 'Felhasználó törlése',
      message: (
        <>
          Biztosan törölni szeretnéd <strong>{user.name}</strong> felhasználót?
          Ez a művelet deaktiválja a felhasználót a rendszerben.
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setError('');
      await userService.deleteUser(user.userId);
      showSuccess('A felhasználó törölve.');
      await loadUsers();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a művelet során'));
    }
  };

  /**
   * „Aktiválás" — CSAK az állapotot állítja, semmi mást [BL-A, 2026-09-15].
   *
   * ⚠️ MI VOLT ITT, ÉS MIÉRT TILOS VISSZAÍRNI: ez a kezelő korábban a teljes felhasználó-módosítást
   * (`userService.updateUser`, `PUT /api/users/{id}`) hívta, de csak HAT mezőt küldött
   * (`name, email, phone, userRoleId, isActive, isApproved`). A szerver a PUT-ban FELTÉTEL NÉLKÜL
   * ír minden mezőt (`backend/PinyoApi/Controllers/UsersController.cs`, `UpdateUser`), tehát a
   * kérésből kimaradók `null`-lá lettek. MÉRVE élő lokális API-n: `hourlyRate 4200 → null`,
   * `bankAccount → null` — egy kattintásra, megerősítő kérdés nélkül, „A felhasználó aktiválva."
   * sikerjelzéssel. Az edzői kifizetés ezután óradíj nélkül számolt, és nem volt hova utalni.
   * A magánóra-díj (`privateLessonFee`) és a preferált helyszín (`venueId`) ugyanígy tűnt el.
   *
   * A szerepkör-feloldás (`userRoles.find(...)`) is innen KERÜLT KI: a részleges PUT-hoz kellett,
   * és ha a szerepkör-lista még nem érkezett meg, ÜRES Guid-ot küldött — a szerver ilyenkor
   * „Érvénytelen szerepkör" 400-at adott, tehát az aktiválás a lista betöltésétől függött.
   */
  const handleReactivate = async (user: UserListItem) => {
    try {
      setError('');
      await userService.reactivateUser(user.userId);
      showSuccess('A felhasználó aktiválva.');
      await loadUsers();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az aktiválás során'));
    }
  };

  const isAdmin = currentUser?.userRole === 'Administrator';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nincs jogosultságod a felhasználók kezeléséhez.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Felhasználó kezelés
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: 200 }}>
              <TextField
                fullWidth
                label="Keresés (név vagy email)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="user-search-input"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 auto' }, minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel>Szerepkör</InputLabel>
                <Select
                  value={filters.roleId || ''}
                  label="Szerepkör"
                  onChange={(e) => handleFilterChange('roleId', e.target.value)}
                  data-testid="role-filter-select"
                >
                  <MenuItem value="" data-value="all">Összes</MenuItem>
                  {userRoles.map((role) => (
                    <MenuItem key={role.userRoleId} value={role.userRoleId} data-value={role.name}>
                      {role.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 auto' }, minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel>Helyszín</InputLabel>
                <Select
                  value={filters.venueId || ''}
                  label="Helyszín"
                  onChange={(e) => handleFilterChange('venueId', e.target.value)}
                >
                  <MenuItem value="">Összes</MenuItem>
                  {Array.isArray(venues) && venues.map((venue) => (
                    <MenuItem key={venue.venueId} value={venue.venueId}>
                      {venue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!searchInput && !filters.search && !filters.roleId && !filters.venueId}
            >
              Törlés
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openDialog('create')}
              data-testid="add-user-button"
            >
              Új felhasználó
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table data-testid="user-table">
          <TableHead>
            <TableRow>
              <TableCell>Név</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Szerepkör</TableCell>
              <TableCell>Preferált helyszín</TableCell>
              <TableCell>Állapot</TableCell>
              <TableCell align="right">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nincs találat
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.userId}
                  data-testid={`user-row-${user.userId}`}
                  sx={{
                    bgcolor: !isProfileComplete(user) ? 'error.light' : 'inherit',
                    '&:hover': {
                      bgcolor: !isProfileComplete(user) ? 'error.main' : 'action.hover',
                    },
                  }}
                >
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell>{user.userRoleDisplayName}</TableCell>
                  <TableCell>{user.venueName || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Aktív' : 'Inaktív'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {/*
                      ⚠️ ÜZEMBEN MINDIG AZ ELSŐ ÁG FUT [kimondva 2026-09-15, K-3].
                      A szülő (`Dashboard.tsx`) a `SettingsManagement`-en át MINDIG átadja az
                      `onEditUser`-t, tehát a szerkesztés élesben a Profil fülre navigál
                      (`UserProfile` / `UserProfileDialog`), és az itteni belső dialógus
                      KIZÁRÓLAG `'create'` módban nyílik ki.
                      A második ág (`openDialog('edit', user)`) így ma HALOTT KÓD — szándékosan
                      megmarad (a komponens önállóan, `onEditUser` nélkül is használható, és a
                      törlése egy nagy UI-blokk átszervezése lenne, külön körben), de aki a
                      belső dialógus SZERKESZTŐ-ágára tesztet ír, tudja: azt az utat a felületen
                      ma nem lehet bejárni. A `__tests__/UserManagement.unsaved.test.tsx`
                      fejléce ugyanezt mondja ki.
                    */}
                    <Tooltip title="Szerkesztés">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (onEditUser) {
                            onEditUser(user.userId);
                          } else {
                            openDialog('edit', user);
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {!user.isActive ? (
                      <Tooltip title="Aktiválás">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleReactivate(user)}
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Törlés">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.userId === currentUser?.userId}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Sorok száma:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} / ${count !== -1 ? count : `több mint ${to}`}`
          }
        />
      </TableContainer>

      {/* User Dialog */}
      <UserDialog
        open={dialogOpen}
        onClose={closeDialog}
        type={dialogType}
        user={selectedUser}
        userRoles={userRoles}
        venues={venues}
        onAction={handleUserAction}
      />
    </Box>
  );
};

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'create' | 'edit' | 'view';
  user: UserListItem | null;
  userRoles: UserRole[];
  venues: any[];
  /** A mentés hibaszövege (a dialógus saját sávjába kerül), vagy `null`/semmi siker esetén. */
  onAction: (action: 'create' | 'edit', data?: any) => Promise<string | null | void> | void;
}

const UserDialog: React.FC<UserDialogProps> = ({
  open,
  onClose,
  type,
  user,
  userRoles,
  venues,
  onAction,
}) => {
  const confirm = useConfirm();
  const [formData, setFormData] = useState<any>(createEmptyUserForm());
  /**
   * A NYITÁSKORI állapot, mint alapvonal — ehhez hasonlítjuk az űrlapot a mentetlen-változás
   * védelemhez. `null` = még nem nyílt ki (ilyenkor a `isFormDirty` soha nem piszkos).
   */
  const [baseline, setBaseline] = useState<any | null>(null);
  /**
   * A PÉNZ-MEZŐKBE GÉPELT nyers szöveg (mezőnév → szöveg) — CSAK MEGJELENÍTÉSRE [BL-2b, 2026-09-15].
   *
   * MIÉRT KELL: a két mező állapota `number | undefined`, tehát egy RÉGI, tizedes érték
   * takarításának első leütése (`"2.8"` → `"2."`) a `parseForintInput`-on `undefined` lett, és a
   * mező KIÜRÜLT — miközben a szerveren még a 2,8 állt. A mentés ezt az `undefined`-ot küldte ki,
   * és a szerver FELTÉTEL NÉLKÜL ír (`backend/PinyoApi/Controllers/UsersController.cs:414-415`
   * `user.HourlyRate = updateUserDto.HourlyRate`), tehát a tárolt óradíj/magánóra-díj NULL lett.
   * A mentendő SZÁM változatlanul a `formData`-ban van, tehát a mentetlen-változás alapvonalának
   * alakja NEM változott.
   */
  const [forintDrafts, setForintDrafts] = useState<{ hourlyRate?: string; privateLessonFee?: string }>({});
  /**
   * Az ŰRLAP SAJÁT hiba-sávja [BL-2b, 2026-09-15]: a képernyő hiba-Alertje a dialógus MÖGÖTT van,
   * tehát a nyitott ablakban nem látszana. A `VenueManagement` űrlapja ugyanezt a mintát használja.
   */
  const [formError, setFormError] = useState('');
  /**
   * A MENTÉST BLOKKOLÓ PÉNZ-HIBA MAGÁN A MEZŐN [M2, 2026-09-15].
   * MI VOLT A BAJ: az őr csak a `formError` sávot töltötte ki (`user-form-error`, `:716`), a két
   * pénz-mező viszont jóval lejjebb van (`:920` / `:953`) — a felhasználó a Mentés megnyomása
   * után SEMMIT nem látott, tehát a mentés néma no-opnak tűnt.
   */
  const [forintFieldErrors, setForintFieldErrors] = useState<{ [key: string]: string }>({});
  // A jelszó-mező SAJÁT hibája (a `formError` sáv MELLETT) — a mező maga is megjelöli magát,
  // különben egy hosszú űrlapon nem látszik, MELYIK mezőt kell javítani [B-W7, 2026-09-16].
  const [passwordFieldError, setPasswordFieldError] = useState('');
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  /** A két pénz-mező DOM-eleme — az őr ide fókuszál és ide görget [M2]. */
  const forintInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const { showError } = useFeedback();
  /** A mezőben MEGJELENÍTETT szöveg: a gépelt nyers alak, ha van, különben a mentendő szám. */
  const forintFieldText = (name: 'hourlyRate' | 'privateLessonFee') =>
    forintDrafts[name] ?? forintFieldValue(formData[name]);

  /**
   * MELYIK PÉNZ-MEZŐ LÁTSZIK ÉPPEN — a szerepkörtől függ (Óradíj = Edző, Magánóra-díj = Játékos,
   * lásd a két `TextField` feltételét lentebb). EGY helyen, mert három dolog múlik rajta: a
   * megjelenítés, a mentés-őr és a szerepkör-váltás takarítása.
   */
  const currentRoleName = userRoles.find(r => r.userRoleId === formData.userRoleId)?.name;
  const isForintFieldVisible = (name: 'hourlyRate' | 'privateLessonFee') =>
    name === 'hourlyRate' ? currentRoleName === 'Coach' : currentRoleName === 'Player';

  /**
   * SZEREPKÖR-VÁLTÁSKOR A MÁR NEM LÁTHATÓ PÉNZ-MEZŐ VÁZLATÁT ELVETJÜK [K-3, 2026-09-15].
   *
   * MI VOLT A BAJ (a 244cd5d 2. validálásának K-3 lelete): a vázlatot CSAK a dialógus megnyitása
   * nullázta (`UserManagement.tsx:604`), a szerepkör-váltás nem (`handleInputChange`, `:649`).
   * Ezért: egy edzőnél félbehagyott „2." óradíj, majd váltás Játékosra → az Óradíj mező
   * KISZERELŐDIK a DOM-ból (`:999`), a mentés-őr viszont (`:675`) továbbra is „Óradíj (Ft)"-tal
   * BLOKKOLT, a `forintInputRefs.current.hourlyRate` pedig már `null` → se fókusz, se görgetés,
   * csak egy hibaszöveg egy NEM LÉTEZŐ mezőről. A mentés így CSAK a szerepkör visszaállításával
   * volt feloldható.
   *
   * ⚠️ A MENTENDŐ SZÁMOT IS VISSZAÁLLÍTJUK AZ ALAPVONALRA, ha a vázlat FÉLBEHAGYOTT volt: a
   * `parseForintInput('2.')` `undefined`, a PUT-ból a mező kimaradna, és a szerver feltétel nélkül
   * ír (`UsersController.cs:414-415`) → a tárolt óradíj NULL lenne. Vagyis a láthatatlan mező
   * NÉMÁN nullázta volna a pénzt — pont a BL-2b adatvesztés, csak rejtve. A BEFEJEZETT (számra
   * parseolható) vázlatnál a `formData` értékét NEM mozdítjuk: azt a meglévő logika viszi ki.
   */
  useEffect(() => {
    if (!open) return;
    const stale = (['hourlyRate', 'privateLessonFee'] as const).filter(name =>
      !isForintFieldVisible(name) && (forintDrafts[name] !== undefined || !!forintFieldErrors[name]));
    if (stale.length === 0) return;

    const unfinished = stale.filter(name => isUnfinishedForintDraft(forintDrafts[name]));
    setForintDrafts(prev => {
      const next = { ...prev };
      stale.forEach(name => { delete next[name]; });
      return next;
    });
    setForintFieldErrors(prev => {
      if (!stale.some(name => prev[name])) return prev;
      const next = { ...prev };
      stale.forEach(name => { next[name] = ''; });
      return next;
    });
    if (unfinished.length > 0) {
      setFormData((prev: any) => {
        const next = { ...prev };
        unfinished.forEach(name => { next[name] = baseline ? baseline[name] : undefined; });
        return next;
      });
    }
    // A hiba-sáv is a nem létező mezőre mutatott volna.
    setFormError(prev => (stale.some(name => prev.includes(name === 'hourlyRate' ? 'Óradíj' : 'Magánóra-díj')) ? '' : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.userRoleId, userRoles, forintDrafts, forintFieldErrors, baseline]);

  /**
   * Az űrlap NULLÁZÁSA + az alapvonal felvétele a dialógus MEGNYITÁSAKOR.
   *
   * ⚠️ Az `open` a függőségek között NEM elhagyható: az „Új felhasználó" gomb csak az `open`-t
   * állítja át (a `dialogType` már `'create'`, a `selectedUser` már `null` egy előző create után),
   * tehát `open` nélkül a hatás NEM futna újra — a félbehagyott, majd elvetett űrlap értékei
   * visszajönnének a következő megnyitáskor, és az alapvonalhoz képest AZONNAL „mentetlennek"
   * látszanának. Így viszont minden megnyitás tiszta lapot és hozzá illő alapvonalat kap.
   */
  useEffect(() => {
    if (!open) return;
    // Minden megnyitás tiszta lapot kap: a korábbi gépelt pénz-vázlat nem szivároghat át.
    setForintDrafts({});
    setFormError('');
    setForintFieldErrors({});
    if (user && (type === 'edit' || type === 'view')) {
      const loaded = userToForm(user, userRoles);
      setFormData(loaded);
      setBaseline(loaded);
    } else if (type === 'create') {
      const blank = createEmptyUserForm();
      setFormData(blank);
      setBaseline(blank);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, type]);

  /**
   * ⚠️ A `userRoles` SZÁNDÉKOSAN NINCS a fenti dep-listában (2026-09-13, validátor-lelet BL-2).
   *
   * A szerepkör-lista a képernyő mountjánál indul (`loadUserRoles`), az „Új felhasználó" gomb
   * viszont NINCS a betöltéshez kapuzva — tehát az admin kinyithatja az ablakot, beírhatja a
   * nevet, és a KÉSVE megjövő lista a reset-hatást újra lefuttatva NULLÁZTA a beírt adatokat.
   * A védelem erre VAK volt: a `formData` és az alapvonal egyszerre nullázódott, tehát a kapu
   * „szabadnak" látta az űrlapot, és semmi nem kérdezett rá.
   *
   * A create-ág nem is IGÉNYLI a szerepköröket (`createEmptyUserForm()` `userRoleId: ''`-t ad),
   * a szerkesztés-ág pedig a lenti, CÉLZOTT hatásból kapja meg a szerepkört, amikor a lista
   * megjön — a többi mező megtartásával. Így egyetlen ág sem veszít beírt adatot.
   */

  /**
   * A KÉSVE megjövő szerepkör-lista utólagos bekötése a SZERKESZTÉS-ágon, adatvesztés nélkül.
   *
   * Csak akkor tölt, ha a választó még ÜRES (tehát a felhasználó nem választott mást), és az
   * alapvonalat IS lépteti — különben a rendszer által kitöltött érték azonnal „mentetlen
   * módosításnak" látszana (hamis riasztás egy érintetlen ablakon).
   */
  useEffect(() => {
    if (!open) return;
    if (!user || type === 'create') return;
    const resolved = resolveUserRoleId(userRoles, user);
    if (!resolved) return;
    setFormData((prev: any) => (prev?.userRoleId ? prev : { ...prev, userRoleId: resolved }));
    setBaseline((prev: any) => (prev && !prev.userRoleId ? { ...prev, userRoleId: resolved } : prev));
  }, [open, user, type, userRoles]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event?: React.MouseEvent | React.FormEvent) => {
    // Prevent any default form submission behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * KÖZTES PÉNZ-VÁZLAT: a mentés MEGÁLL, a MEZŐ MEGNEVEZÉSÉVEL [BL-2b, 2026-09-15].
     *
     * MI VOLT A BAJ (mérve): egy RÉGI, tizedes órabér takarításának első leütése (`"2.8"` → `"2."`)
     * elfogadott leütés (J-3), de a `parseForintInput` `undefined`-ot ad rá — a PUT-ból a mező
     * KIMARADT, és a szerver feltétel nélkül ír (`UsersController.cs:414-415`), tehát a tárolt
     * órabér/magánóra-díj NULL lett. Ez az edző MINDEN további kifizetés-számítását díj nélkül
     * futtatta volna.
     *
     * A SZÁNDÉKOS TÖRLÉS ÉRINTETLEN: az üres mező (`""`) számjegy-alakú, tehát nem köztes vázlat.
     * A hibát a dialógus SAJÁT hiba-sávja mutatja, hogy a nyitott ablakban is látszódjon.
     */
    // ⚠️ CSAK A LÁTHATÓ MEZŐK [K-3, 2026-09-15]: egy szerepkör-váltás után kiszerelődött mezőre
    // mutató hiba feloldhatatlan (nincs mire fókuszálni, és a felhasználó nem is látja, mit
    // javítson) — a vázlatát a fenti hatás már el is vetette.
    const unfinishedField = ([
      ['hourlyRate', 'Óradíj (Ft)'],
      ['privateLessonFee', 'Magánóra-díj (Ft)'],
    ] as const).find(([name]) => isForintFieldVisible(name) && isUnfinishedForintDraft(forintDrafts[name]));
    if (unfinishedField) {
      /**
       * ⚠️ A BLOKKOLÁS NEM LEHET NÉMA [M2, 2026-09-15]: a sáv MELLETT toast is megy, és MAGA A
       * MEZŐ is megjelöli magát (piros keret + súgó), oda görgetve és fókuszálva.
       */
      const message = unfinishedForintMessage(unfinishedField[1]);
      setFormError(message);
      showError(message);
      setForintFieldErrors(prev => ({ ...prev, [unfinishedField[0]]: message }));
      const input = forintInputRefs.current[unfinishedField[0]];
      // jsdom-ban a `scrollIntoView` nem létezik — a `?.` NEM kozmetika.
      input?.scrollIntoView?.({ block: 'center' });
      input?.focus?.();
      return;
    }
    /**
     * JELSZÓ-ELŐELLENŐRZÉS A KÖZÖS TÜKÖRREL [B-W7, 2026-09-16].
     *
     * MI VOLT A BAJ: az ablak semmit nem ellenőrzött és semmit nem is mondott a jelszóról. A
     * szerver 2026-09-15 óta erősséget kér, tehát az admin egy gyenge jelszóval létrehozott
     * felhasználót „valamiért nem tudott" létrehozni, és a szabályt próbálgatással kellett
     * kitalálnia.
     *
     * ⚠️ AZ INVARIÁNS: a kliens SOHA nem lehet szigorúbb a szervernél — ezért NEM saját
     * feltételek vannak itt, hanem a szerver-tükör (`utils/passwordPolicy.ts`). A szerver 400-a
     * továbbra is kijut (`handleUserAction` → `apiErrorMessage`), tehát ha a tükör valaha
     * LAZÁBB lenne a szervernél, a felhasználó akkor is megtudja, mi a baj.
     */
    if (type === 'create') {
      const passwordError = passwordPolicyError((formData as CreateUserRequest).password || '');
      if (passwordError) {
        setPasswordFieldError(passwordError);
        setFormError(passwordError);
        showError(passwordError);
        // jsdom-ban a `scrollIntoView` nem létezik — a `?.` NEM kozmetika.
        passwordInputRef.current?.scrollIntoView?.({ block: 'center' });
        passwordInputRef.current?.focus?.();
        return;
      }
    }

    setFormError('');
    setForintFieldErrors({});
    setPasswordFieldError('');

    // Ensure the button is enabled and we can submit
    try {
      const failure = await onAction(type as 'create' | 'edit', formData);
      // [7. javító kör — validátor F3] a szerverhiba a NYITVA maradó dialógus saját sávjában is
      if (typeof failure === 'string' && failure) setFormError(failure);
    } catch (error) {
      console.error('Submit action failed:', error);
      // Don't rethrow to prevent unhandled errors
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'create': return 'Új felhasználó létrehozása';
      case 'edit': return 'Felhasználó szerkesztése';
      case 'view': return 'Felhasználó adatai';
      default: return '';
    }
  };

  const isViewMode = type === 'view';

  /**
   * Mentetlen módosítás: a nyitáskori alapvonalhoz hasonlítva.
   *
   * A `open &&` NEM elhagyható: a dialógus zárva is MOUNTOLVA marad a szülőben (a `UserDialog`
   * mindig renderelődik, csak a MUI `Dialog` nem jelenít meg semmit), a beírt — majd elvetett —
   * értékekkel. Nélküle egy zárt ablak is „mentetlen adatot" jelentene, és blokkolná a
   * fül-váltást.
   *
   * NÉZET-MÓD: ott minden mező `disabled`, tehát piszkos sem lehet — nem jelentünk be semmit.
   * (A `'view'` mód ma egyébként nem is elérhető: az `openDialog` csak `'create'`-tel és
   * `'edit'`-tel hívódik.)
   */
  const formLabel = type === 'create' ? 'Új felhasználó' : 'Felhasználó szerkesztése';
  const isDirty = open && !isViewMode && isFormDirty(baseline, formData);
  useUnsavedChanges(isDirty, formLabel);

  /**
   * MINDEN záró út ezen megy át: a `Dialog onClose`-a (ESC + backdrop) és a lábléc „Bezárás"
   * gombja. A SIKERES mentés utáni zárás szándékosan NEM ezen megy át: azt a szülő
   * `handleUserAction` hívja a nyers `closeDialog()`-gal, tehát mentés után nincs kérdés.
   */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm(formLabel)))) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="user-dialog"
      sx={{
        '& .MuiDialog-container': {
          zIndex: 1300,
        },
        '& .MuiDialog-paper': {
          zIndex: 1301,
          position: 'relative',
        },
        '& .MuiBackdrop-root': {
          zIndex: 1299,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            zIndex: 1299,
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <DialogTitle data-testid="user-dialog-title">{getTitle()}</DialogTitle>
      <DialogContent
        data-testid="user-dialog-content"
        sx={{
          position: 'relative',
          zIndex: 1301,
          padding: 3,
          '& .MuiFormControl-root': {
            position: 'relative',
            zIndex: 'auto',
          },
          '& .MuiInputBase-root': {
            position: 'relative',
            zIndex: 'auto',
          },
        }}
      >
        <Box
            component="form"
            id="user-form"
            sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            data-testid="create-user-form"
            onSubmit={handleSubmit}
            noValidate
          >
            {formError && (
              <Alert severity="error" data-testid="user-form-error" onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Teljes név"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isViewMode}
              required
              data-testid="user-name-input"
              name="name"
              id="user-name-field"
              inputProps={{
                'data-testid': 'user-name-input-field',
                autoComplete: 'name',
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={isViewMode}
              required
              data-testid="user-email-input"
              name="email"
              id="user-email-field"
              inputProps={{
                'data-testid': 'user-email-input-field',
                autoComplete: 'email',
              }}
            />
            <TextField
              fullWidth
              label="Telefon"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={isViewMode}
              required
              data-testid="user-phone-input"
              name="phone"
              id="user-phone-field"
              inputProps={{
                'data-testid': 'user-phone-input-field',
                autoComplete: 'tel',
              }}
            />
            <FormControl fullWidth required data-testid="user-role-form-control">
              <InputLabel id="user-role-label">Szerepkör</InputLabel>
              <Select
                value={formData.userRoleId}
                label="Szerepkör"
                labelId="user-role-label"
                onChange={(e) => handleInputChange('userRoleId', e.target.value)}
                disabled={isViewMode}
                data-testid="user-role-select"
                name="userRoleId"
                id="user-role-field"
                inputProps={{
                  'data-testid': 'user-role-select-input',
                  'aria-label': 'Szerepkör kiválasztása',
                }}
                MenuProps={{
                  disablePortal: false,
                  sx: {
                    zIndex: 9999,
                    '& .MuiPaper-root': {
                      zIndex: 9999,
                    },
                    '& .MuiMenu-list': {
                      zIndex: 9999,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-root': {
                    cursor: 'pointer',
                  },
                }}
              >
                {userRoles.map((role) => (
                  <MenuItem key={role.userRoleId} value={role.userRoleId} data-value={role.name}>
                    {role.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentRoleName === 'Coach' && (
              <TextField
                fullWidth
                label="Bankszámlaszám (opcionális)"
                value={formData.bankAccount || ''}
                onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                disabled={isViewMode}
                data-testid="user-bank-account-input"
                name="bankAccount"
                id="user-bank-account-field"
                inputProps={{
                  'data-testid': 'user-bank-account-input-field',
                }}
              />
            )}
            {currentRoleName === 'Player' && (
              <>
                <TextField
                  fullWidth
                  label="Számlázási név (opcionális)"
                  value={formData.billingName || ''}
                  onChange={(e) => handleInputChange('billingName', e.target.value)}
                  disabled={isViewMode}
                  data-testid="user-billing-name-input"
                  name="billingName"
                  id="user-billing-name-field"
                  inputProps={{
                    'data-testid': 'user-billing-name-input-field',
                  }}
                  helperText="Számlázáshoz használt név"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Irányítószám"
                    value={formData.billingZipCode || ''}
                    onChange={(e) => handleInputChange('billingZipCode', e.target.value)}
                    disabled={isViewMode}
                    name="billingZipCode"
                    sx={{ width: '30%' }}
                  />
                  <TextField
                    label="Város"
                    value={formData.billingCity || ''}
                    onChange={(e) => handleInputChange('billingCity', e.target.value)}
                    disabled={isViewMode}
                    name="billingCity"
                    sx={{ width: '70%' }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Utca, házszám"
                  value={formData.billingStreetAddress || ''}
                  onChange={(e) => handleInputChange('billingStreetAddress', e.target.value)}
                  disabled={isViewMode}
                  name="billingStreetAddress"
                />
              </>
            )}
            <FormControl fullWidth data-testid="user-venue-form-control">
              <InputLabel id="user-venue-label">Preferált helyszín (opcionális)</InputLabel>
              <Select
                value={formData.venueId || ''}
                label="Preferált helyszín (opcionális)"
                labelId="user-venue-label"
                onChange={(e) => handleInputChange('venueId', e.target.value || undefined)}
                disabled={isViewMode}
                data-testid="user-venue-select"
                name="venueId"
                id="user-venue-field"
                inputProps={{
                  'data-testid': 'user-venue-select-input',
                  'aria-label': 'Helyszín kiválasztása',
                }}
                MenuProps={{
                  disablePortal: false,
                  sx: {
                    zIndex: 9999,
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Nincs kiválasztva</em>
                </MenuItem>
                {Array.isArray(venues) && venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isForintFieldVisible('hourlyRate') && (
              <TextField
                fullWidth
                label="Óradíj (Ft)"
                // `type="text"` + `inputMode="numeric"`, NEM `type="number"` [BL-2, 2026-09-14]: a
                // szám-input a `2.800` alakot ÉRVÉNYES számként adta át, a `Number('2.800')` pedig
                // 2,8-at ad — a magyarul gépelő adminisztrátor után EZREDANNYI óradíj került a
                // szerverre, NÉMÁN (a mező közben változatlanul „2.800"-at mutatott). A mező súgója
                // maga is „egész szám"-ot kér; a mérést és a döntést lásd a `utils/money.ts`-ben.
                type="text"
                value={forintFieldText('hourlyRate')}
                onChange={(e) => {
                  const value = e.target.value;
                  // Nem-számjegy leütés: EL SEM tároljuk, a mező a korábbi tartalmát mutatja.
                  // A MOSTANI érték a MEGJELENÍTETT szöveg (a vázlat, ha van), hogy egy RÉGI,
                  // tizedes óradíj takarítható legyen [J-3], és a köztes alak LÁTSZÓDJON [BL-2b].
                  if (!isForintInputAccepted(value, forintFieldText('hourlyRate'))) return;
                  // A mező-szintű hiba a JAVÍTÁS ELSŐ leütésére eltűnik [M2].
                  setForintFieldErrors(prev => ({ ...prev, hourlyRate: '' }));
                  setForintDrafts(prev => ({ ...prev, hourlyRate: value }));
                  handleInputChange('hourlyRate', parseForintInput(value));
                }}
                inputRef={(el: HTMLInputElement | null) => { forintInputRefs.current.hourlyRate = el; }}
                error={!!forintFieldErrors.hourlyRate}
                disabled={isViewMode}
                data-testid="user-hourly-rate-input"
                name="hourlyRate"
                id="user-hourly-rate-field"
                inputProps={{
                  'data-testid': 'user-hourly-rate-input-field',
                  inputMode: 'numeric',
                }}
                // A `input[type=number]` pörgető-nyilait elrejtő `sx` innen ELTŰNT: a mező
                // `type="text"`, nincs mit elrejteni [BL-2].
                // A mentés-őr hibája ELŐZI a magyarázó súgót [M2] — a mező alatt egy sor van.
                helperText={forintFieldErrors.hourlyRate || "0-nál nagyobb egész szám"}
              />
            )}
            {isForintFieldVisible('privateLessonFee') && (
              <TextField
                fullWidth
                label="Magánóra-díj (Ft)"
                // `type="text"` + `inputMode="numeric"` [BL-2] — lásd az Óradíj mezőt: a
                // `Number('2.800')` 2,8-at adott, tehát a magyar írásmód ezredannyit mentett.
                type="text"
                value={forintFieldText('privateLessonFee')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!isForintInputAccepted(value, forintFieldText('privateLessonFee'))) return;
                  setForintFieldErrors(prev => ({ ...prev, privateLessonFee: '' }));
                  setForintDrafts(prev => ({ ...prev, privateLessonFee: value }));
                  handleInputChange('privateLessonFee', parseForintInput(value));
                }}
                disabled={isViewMode}
                data-testid="user-private-lesson-fee-input"
                name="privateLessonFee"
                id="user-private-lesson-fee-field"
                inputProps={{ 'data-testid': 'user-private-lesson-fee-input-field', inputMode: 'numeric' }}
                inputRef={(el: HTMLInputElement | null) => { forintInputRefs.current.privateLessonFee = el; }}
                error={!!forintFieldErrors.privateLessonFee}
                helperText={forintFieldErrors.privateLessonFee
                  || "A játékos magánóra-díja; a kedvezmény alacsonyabb szám. Üresen hagyható."}
              />
            )}
            {type === 'create' && (
              <TextField
                fullWidth
                label="Jelszó"
                type="password"
                value={(formData as CreateUserRequest).password || ''}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                data-testid="user-password-input"
                name="password"
                id="user-password-field"
                inputProps={{
                  'data-testid': 'user-password-input-field',
                  autoComplete: 'new-password',
                }}
                inputRef={passwordInputRef}
                // A SÚGÓ = A SZERVER MONDATA, szó szerint [B-W7, 2026-09-16]. Korábban semmilyen
                // súgó nem volt itt: az admin csak a szerver 400-ából tudhatta meg a szabályt.
                error={!!passwordFieldError}
                helperText={passwordFieldError || PASSWORD_RULE_MESSAGE}
              />
            )}
            {type === 'edit' && (
              <FormControl fullWidth data-testid="user-status-form-control">
                <InputLabel id="user-status-label">Állapot</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  label="Állapot"
                  labelId="user-status-label"
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                  data-testid="user-status-select"
                  name="isActive"
                  id="user-status-field"
                  inputProps={{
                    'data-testid': 'user-status-select-input',
                    'aria-label': 'Állapot kiválasztása',
                  }}
                  MenuProps={{
                    disablePortal: false,
                    sx: {
                      zIndex: 9999,
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="active">Aktív</MenuItem>
                  <MenuItem value="inactive">Inaktív</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
      </DialogContent>
      <DialogActions
        data-testid="user-dialog-actions"
        sx={{
          position: 'relative',
          zIndex: 1302,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={handleClose}
          data-testid={type === 'create' ? 'create-user-cancel' : 'cancel-user-button'}
          sx={{
            position: 'relative',
            zIndex: 1303,
            pointerEvents: 'auto',
          }}
        >
          Bezárás
        </Button>
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            type="submit"
            form="user-form"
            data-testid={type === 'create' ? 'create-user-submit' : 'edit-user-button'}
            sx={{
              position: 'relative',
              zIndex: 1303,
              pointerEvents: 'auto',
              minWidth: '100px',
              '&:not(:disabled)': {
                cursor: 'pointer',
              },
              '&:hover': {
                transform: 'none', // Prevent animation that might interfere with automation
              },
            }}
            disableElevation={false}
            disableRipple={false}
          >
            {type === 'create' ? 'Létrehozás' : 'Mentés'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserManagement;